import { type ReactNode, useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, KeyRound, Users, Shield, Settings, LogOut,
  Activity, CreditCard, Menu, X, Building2, Bell,
  Check, ChevronLeft, ChevronRight, FileKey, PenLine, UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useInvitations } from '../../hooks/useInvitations';
import { usePageTitle } from '../../hooks/usePageTitle';
import { RECURSO_CERTIFICADOS, temRecurso } from '../../utils/features';
import { formatarNome, nomeDoPapel, nomeOuEmail } from '../../utils/nomes';
import { useFeedback } from '../common/Feedback';
import './AppLayout.css';

interface AppLayoutProps {
  children: ReactNode;
}

interface ItemDoMenu { name: string; path: string; icon: LucideIcon }
interface GrupoDoMenu { titulo: string; itens: ItemDoMenu[] }

export function AppLayout({ children }: AppLayoutProps) {
  const { logout, user, membership } = useAuth();
  const { avisar } = useFeedback();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBellOpen, setIsBellOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  const { invitations, fetchInvitations, acceptInvitation, rejectInvitation } = useInvitations();
  const [processingInvite, setProcessingInvite] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchInvitations();
    }
  }, [user, fetchInvitations]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAccountOpen(false);
  }, [location.pathname]);

  // Fechar os menus do cabeçalho ao clicar fora ou com Esc
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setIsBellOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setIsAccountOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setIsBellOpen(false);
      setIsAccountOpen(false);
      setIsMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const toggleSidebar = () => {
    const next = !isSidebarCollapsed;
    setIsSidebarCollapsed(next);
    localStorage.setItem('sidebar_collapsed', String(next));
  };


  const handleAcceptInvite = async (id: string) => {
    setProcessingInvite(id);
    try {
      await acceptInvitation(id);
      setIsBellOpen(false);
      window.location.href = '/organizations';
    } catch (err: any) {
      avisar(err.message || 'Erro ao aceitar convite.', 'erro');
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleRejectInvite = async (id: string) => {
    setProcessingInvite(id);
    try {
      await rejectInvitation(id);
    } finally {
      setProcessingInvite(null);
    }
  };

  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';

  // A5: itens agrupados pelo uso; o de todo dia (Credenciais) logo no topo.
  const navGroups: GrupoDoMenu[] = [
    {
      titulo: 'Operação',
      itens: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'Credenciais', path: '/credentials', icon: KeyRound },
        ...(temRecurso(membership, RECURSO_CERTIFICADOS) ? [
          { name: 'Certificados', path: '/certificates', icon: FileKey },
          { name: 'Assinaturas', path: '/signatures', icon: PenLine },
        ] : []),
      ],
    },
    {
      titulo: 'Pessoas e acesso',
      itens: [
        { name: 'Membros', path: '/members', icon: Users },
        ...(isAdmin ? [
          { name: 'Grupos', path: '/groups', icon: Shield },
          { name: 'Registro de atividades', path: '/audit-logs', icon: Activity },
        ] : []),
        { name: 'Organizações', path: '/organizations', icon: Building2 },
      ],
    },
    {
      titulo: 'Conta',
      itens: [
        { name: 'Configurações', path: '/settings', icon: Settings },
        ...(membership?.role === 'owner' ? [
          { name: 'Plano e cobrança', path: '/billing', icon: CreditCard },
        ] : []),
      ],
    },
  ];

  const currentItem = navGroups.flatMap(g => g.itens).find(i => i.path === location.pathname);
  // A6: o nome da página vai para a aba do navegador ("Membros · Lictor") e aparece uma vez só, no h1 da página.
  usePageTitle(currentItem?.name ?? 'Painel');

  const displayName = nomeOuEmail(user?.name, user?.email);
  const initial = (displayName || '?').charAt(0).toUpperCase();

  return (
    <div className="app-layout-container">
      {/* B4: primeiro foco da página */}
      <a href="#conteudo" className="pular-para-conteudo">Pular para o conteúdo</a>

      {/* Mobile Overlay */}
      <div
        className={`mobile-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`app-sidebar ${isMobileMenuOpen ? 'open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Logo + toggle */}
        <div className="sidebar-header" style={{ marginBottom: 'var(--space-6)' }}>
          <Link to="/" className="sidebar-logo">
            <img src="/logo.png" alt="Lictor" className="sidebar-logo-img" />
            <span className="sidebar-logo-text app-sidebar-label">
              Lictor
              <span className="sidebar-logo-beta-badge">BETA</span>
            </span>
          </Link>
          <div className="sidebar-actions">
            <button
              className="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Fechar menu"
              style={{ display: isMobileMenuOpen ? 'flex' : 'none' }}
            >
              <X size={20} />
            </button>
            <button className="sidebar-toggle-btn" onClick={toggleSidebar} title={isSidebarCollapsed ? 'Expandir menu' : 'Recolher menu'} aria-label={isSidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}>
              {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        </div>


        {/* Nav links */}
        <nav aria-label="Menu principal" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', flex: 1 }}>
          {navGroups.filter(g => g.itens.length > 0).map((group) => (
            <div key={group.titulo} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              <span className="sidebar-group-title app-sidebar-label">{group.titulo}</span>
              {group.itens.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={isSidebarCollapsed ? item.name : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-3)',
                      justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                      textDecoration: 'none',
                      borderRadius: 'var(--radius-md)',
                      color: isActive ? 'var(--color-primary-400)' : 'var(--color-text-secondary)',
                      background: isActive ? 'var(--color-primary-900)' : 'transparent',
                      fontWeight: isActive ? 'var(--font-medium)' : 'normal',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    <Icon size={18} style={{ flexShrink: 0 }} />
                    <span className="app-sidebar-label">{item.name}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ marginTop: 'auto', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border-default)' }}>
          <button
            onClick={logout}
            title="Sair"
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
              justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
              width: '100%', padding: 'var(--space-3)', background: 'transparent',
              color: 'var(--color-error)', cursor: 'pointer', border: 'none',
              textAlign: 'left', fontSize: 'var(--text-md)', borderRadius: 'var(--radius-md)',
              transition: 'background 0.15s',
            }}
          >
            <LogOut size={18} style={{ flexShrink: 0 }} />
            <span className="app-sidebar-label">Sair</span>
          </button>
        </div>
      </aside>

      <main className="app-main">
        {/* Header */}
        <header className="app-header">
          <div className="app-header-left">
            <button
              className="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Abrir menu"
            >
              <Menu size={24} />
            </button>
            {/* A6: na barra de cima, a organização em que se está; o nome da página fica só no h1. */}
            <div className="app-header-org" title={membership?.org_name}>
              <Building2 size={16} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
              <span>{membership?.org_name}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            {/* Sino de notificações */}
            <div ref={bellRef} style={{ position: 'relative' }}>
              <button
                className="notification-bell-btn"
                onClick={() => setIsBellOpen(!isBellOpen)}
                title="Notificações e convites"
                aria-label="Notificações e convites"
                aria-expanded={isBellOpen}
              >
                <Bell size={18} />
                {invitations.length > 0 && (
                  <span className="notification-badge">{invitations.length}</span>
                )}
              </button>

              {isBellOpen && (
                <div className="notification-dropdown">
                  <div className="notification-dropdown-header">
                    Convites Pendentes
                  </div>
                  {invitations.length === 0 ? (
                    <div className="notification-empty">Nenhum convite pendente</div>
                  ) : (
                    invitations.map(inv => (
                      <div key={inv.id} className="notification-item">
                        <div>
                          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)' }}>
                            {inv.org_name}
                          </div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                            Convidado por <strong>{formatarNome(inv.invited_by) || inv.invited_by}</strong> · {nomeDoPapel(inv.role)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <button
                            onClick={() => handleAcceptInvite(inv.id)}
                            disabled={processingInvite === inv.id}
                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '6px 12px', minHeight: '36px', background: 'var(--color-primary-600)', border: 'none', borderRadius: 'var(--radius-md)', color: 'white', cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 'bold' }}
                          >
                            <Check size={12} /> Aceitar
                          </button>
                          <button
                            onClick={() => handleRejectInvite(inv.id)}
                            disabled={processingInvite === inv.id}
                            aria-label="Recusar convite"
                            title="Recusar convite"
                            style={{ padding: '6px 12px', minHeight: '36px', background: 'transparent', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-md)', color: 'var(--color-error)', cursor: 'pointer', fontSize: 'var(--text-xs)' }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* M8: menu da conta no avatar */}
            <div ref={accountRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="account-btn"
                onClick={() => setIsAccountOpen(v => !v)}
                aria-haspopup="menu"
                aria-expanded={isAccountOpen}
                aria-label="Menu da conta"
              >
                <span className="user-name-display">{displayName}</span>
                <span className="account-avatar">{initial}</span>
              </button>

              {isAccountOpen && (
                <div className="notification-dropdown account-dropdown" role="menu">
                  <div className="notification-dropdown-header" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ overflowWrap: 'anywhere' }}>{displayName}</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', fontWeight: 'normal', overflowWrap: 'anywhere' }}>
                      {user?.email}{membership?.role ? ` · ${nomeDoPapel(membership.role)}` : ''}
                    </span>
                  </div>
                  <div style={{ padding: 'var(--space-1)' }}>
                    <Link to="/settings?aba=perfil" role="menuitem" className="item-do-menu" style={{ color: 'var(--color-text-primary)' }}>
                      <UserRound size={16} /> Perfil e senha
                    </Link>
                    {isAdmin && (
                      <Link to="/settings?aba=organizacao" role="menuitem" className="item-do-menu" style={{ color: 'var(--color-text-primary)' }}>
                        <Building2 size={16} /> Organização
                      </Link>
                    )}
                    {membership?.role === 'owner' && (
                      <Link to="/billing" role="menuitem" className="item-do-menu" style={{ color: 'var(--color-text-primary)' }}>
                        <CreditCard size={16} /> Plano e cobrança
                      </Link>
                    )}
                    <button type="button" role="menuitem" className="item-do-menu" onClick={logout} style={{ color: 'var(--color-error)' }}>
                      <LogOut size={16} /> Sair
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div id="conteudo" tabIndex={-1} className="app-content" style={{ outline: 'none' }}>
          {children}
        </div>
      </main>
    </div>
  );
}
